import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ButtonMantine, Group, Modal, Stack, Title, notifications } from 'ui';

import { useFormListValidation } from '../../../..';
import { useShowAccountCodeByCode } from '../../../../api';
import { getErrorMessage_v2 } from '../../../../utils';
import { AssignAccountCodeInfoBox } from './AssignAccountCodeInfoBox';
import {
  AccountCodeFeatures,
  EditRemoveAccountModalBaseProp,
  ProductItemProps,
} from './AssignAccountCodes.types';
import { HistoricalTransactionRadio } from './HistoricalTransactionRadio';
import { NewOldAccountCodeDisplay } from './NewOldAccountCodeDisplay';
import {
  invalidationKey,
  useAssignAccountCodes,
  useGetHistoricalTransactionCount,
  useUpdateHistoricalTransactions,
} from './hooks';
import { AccountCodeSearchForm } from './types';

interface RemoveAccountCodeModalProps extends EditRemoveAccountModalBaseProp {
  onSuccess: () => void;
}
type RemoveAccountCodeContentProps = {
  feature: AccountCodeFeatures;
  productItemProps?: ProductItemProps;
  accountCode?: string | null;
  causeId: string;
  step: 'remove_code' | 'remove_historical_transactions';
  storeItemId?: string;
};

type RemoveAccountCodeFooterProps = {
  onClose: () => void;
  setStep: (step: 'remove_code' | 'remove_historical_transactions') => void;
  accountCode?: string | null;
  causeId: string;
  feature: AccountCodeFeatures;
  storeItemId?: string;
  step: 'remove_code' | 'remove_historical_transactions';
  onSuccess: () => void;
  setIsLoading: (isLoading: boolean) => void;
};

const RemoveAccountCodeFooter = ({
  onClose,
  setStep,
  causeId,
  feature,
  storeItemId,
  step,
  onSuccess,
  setIsLoading,
}: RemoveAccountCodeFooterProps) => {
  const { data: historicalTransactionCount } = useGetHistoricalTransactionCount({
    causeId,
    feature,
    storeItemId,
  });

  const { useGetForm } = useFormListValidation<AccountCodeSearchForm>();
  const editHistoricalForm = useGetForm('edit_historical_transaction');

  const { mutate: updateAccountCode, isLoading: isAssigninLoading } = useAssignAccountCodes({
    feature,
  });
  const { mutate: updateHistoricalTransactions, isLoading: isUpdateHistorical } =
    useUpdateHistoricalTransactions({ feature });
  // const { options, handleUpdateStoreCause, updateStoreItem, updateLedgerCause, isLoading } =

  const queryClient = useQueryClient();
  const handleSaveSuccess = useCallback(() => {
    onClose();
    onSuccess();
    notifications.show({
      message: 'Account code updated successfully',
      variant: 'success',
    });
    queryClient.invalidateQueries({
      queryKey: invalidationKey({ feature, causeId, storeItemId }),
    });
  }, [onClose, queryClient, feature, causeId, storeItemId, onSuccess]);

  const handleRemove = useCallback(
    (toUpdateHistoricalTransactions?: boolean) => {
      updateAccountCode(
        { accountCode: null, causeId, storeItemId },
        {
          onSuccess() {
            if (toUpdateHistoricalTransactions) {
              updateHistoricalTransactions(
                { accountCodeId: null, causeId, storeItemId },
                {
                  onSuccess() {
                    handleSaveSuccess();
                  },
                  onError(error) {
                    notifications.show({
                      message: getErrorMessage_v2(error),
                      variant: 'error',
                    });
                  },
                }
              );
            } else {
              handleSaveSuccess();
            }
          },
          onError(error) {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    },
    [causeId, storeItemId, updateAccountCode, updateHistoricalTransactions, handleSaveSuccess]
  );

  const onRemoveClick = useCallback(() => {
    if (step === 'remove_code') {
      if (historicalTransactionCount && historicalTransactionCount.count > 0) {
        return setStep?.('remove_historical_transactions');
      } else {
        return handleRemove();
      }
    }

    if (step === 'remove_historical_transactions') {
      const isAppliedToAllTransactions = editHistoricalForm?.values?.applyTo === 'all';
      return handleRemove(isAppliedToAllTransactions);
    }
  }, [
    historicalTransactionCount,
    editHistoricalForm?.values?.applyTo,
    handleRemove,
    setStep,
    step,
  ]);

  const isLoading = useMemo(
    () => isAssigninLoading || isUpdateHistorical,
    [isAssigninLoading, isUpdateHistorical]
  );

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return (
    <Group justify="flex-end">
      <ButtonMantine
        size="md"
        variant="subtle"
        onClick={step === 'remove_code' ? onClose : () => setStep('remove_code')}
      >
        Cancel
      </ButtonMantine>
      <ButtonMantine size="md" color="cerise" onClick={onRemoveClick}>
        Remove Code
      </ButtonMantine>
    </Group>
  );
};

const RemoveAccountCodeContent = ({
  feature,
  productItemProps,
  accountCode,
  causeId,
  step,
  storeItemId,
}: RemoveAccountCodeContentProps) => {
  const { data: accountCodeData } = useShowAccountCodeByCode({
    causeId,
    accountCode: accountCode,
  });
  const { data: historicalTransactionCount } = useGetHistoricalTransactionCount({
    causeId,
    storeItemId,
    feature,
  });

  return (
    <Stack>
      {step === 'remove_code' && (
        <Title order={4}>Are you sure you want to remove this code?</Title>
      )}
      <AssignAccountCodeInfoBox feature={feature} productItemProps={productItemProps} />
      <NewOldAccountCodeDisplay causeId={causeId} accountCode={accountCodeData} isEdit={false} />
      {step === 'remove_historical_transactions' && (
        <HistoricalTransactionRadio
          count={historicalTransactionCount?.count}
          isRemoving
          feature={feature}
        />
      )}
    </Stack>
  );
};

const RemoveAccountCodeModal = ({
  causeId,
  feature,
  opened,
  onClose,
  productItemProps,
  accountCode,
  storeItemId,
  onSuccess,
}: RemoveAccountCodeModalProps) => {
  const isLoadingRef = useRef(false);

  const [step, setStep] = useState<'remove_code' | 'remove_historical_transactions'>('remove_code');

  const setIsLoading = useCallback((isLoading: boolean) => {
    isLoadingRef.current = isLoading;
  }, []);

  return (
    <Modal
      size="lg"
      title="Remove Account Code"
      opened={opened}
      onClose={() => {
        if (isLoadingRef.current) return;
        onClose();
      }}
    >
      <Stack>
        <RemoveAccountCodeContent
          feature={feature}
          productItemProps={productItemProps}
          storeItemId={storeItemId}
          accountCode={accountCode}
          causeId={causeId}
          step={step}
        />
        <RemoveAccountCodeFooter
          onClose={onClose}
          setStep={setStep}
          accountCode={accountCode}
          causeId={causeId}
          feature={feature}
          step={step}
          storeItemId={storeItemId}
          onSuccess={onSuccess}
          setIsLoading={setIsLoading}
        />
      </Stack>
    </Modal>
  );
};

export { RemoveAccountCodeModal };
